/**
 * `RESET-PASSWORD`, R1 (D-1024-10, Q4): **where a password-reset link LANDS, with one author, and why the token is in the
 * FRAGMENT.**
 *
 * The invitation's link carries its token in the path (`/invitation/{token}`), and a path reaches every server that
 * logs a request line. A reset link is a credential for one use that changes the password of a bank seat, so it rides the
 * URL **fragment**: `{origin}/nouveau-mot-de-passe#{token}`. The browser never sends a fragment, so no access log, no
 * proxy and no `Referer` header ever holds it, and the screen strips it from the address bar with
 * `history.replaceState` as soon as it has read it. Every call that carries the token afterwards is a `POST` BODY.
 *
 * **A path with two authors has no author** (`invitation-path.ts`'s lesson, `S2-4`): the api writes this link into a mail
 * and the bank's screen resolves it, so the segment is declared here once and both read it.
 */

/** The one segment, with no slashes, so a caller cannot double one. */
export const PASSWORD_RESET_PATH_SEGMENT = "nouveau-mot-de-passe";

/** The route the screen mounts, fragment apart: `/nouveau-mot-de-passe`. */
export const PASSWORD_RESET_ROUTE = `/${PASSWORD_RESET_PATH_SEGMENT}`;

/** What a reset link carries after the origin: `/nouveau-mot-de-passe#{token}`. */
export function passwordResetPathFor(token: string): string {
  return `${PASSWORD_RESET_ROUTE}#${token}`;
}

/** The token a location's hash carries, or `null`. The hash includes its leading `#`, as `location.hash` does. */
export function passwordResetTokenFrom(hash: string): string | null {
  if (!hash.startsWith("#")) return null;
  const token = hash.slice(1);
  return /^[0-9a-f]{64}$/u.test(token) ? token : null;
}
